import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from "react";
import { nationLogo, dgiLogo, checLogo, pnudLogo, bamadaLogo, spiderLogo, sodiamaLogo, unicefLogo, ufm, unfLogo, tsenaLogo, omsLogo, somacouLogo, fmfpLogo, smefLogo, vigieLogo, colasLogo, leloftLogo, residenceLogo, quantumLogo, pmiLogo, liquimolyLogo, dasscompanyLogo } from '../../assets/images';

const Partenaires = () => {
  const { i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');
  const trackRef = useRef(null);
  const containerRef = useRef(null);

  const partners = [
    { name: "VIGIE", logo: vigieLogo },
    { name: "SMEF", logo: smefLogo },
    { name: "UNICEF", logo: unicefLogo },
    { name: "OMS", logo: omsLogo },
    { name: "FNUAP", logo: unfLogo },
    { name: "DGI", logo: dgiLogo },
    { name: "CHEC", logo: checLogo },
    { name: "SPIDER", logo: spiderLogo },
    { name: "Tsena Express", logo: tsenaLogo },
    { name: "UFM", logo: ufm },
    { name: "SOMACOU", logo: somacouLogo },
    { name: "FMFP", logo: fmfpLogo },
    { name: "COLAS", logo: colasLogo },
    { name: "LeLoft", logo: leloftLogo },
    { name: "Residence", logo: residenceLogo },
    { name: "Quantum", logo: quantumLogo },
    { name: "PMI", logo: pmiLogo },
    { name: "Liquimoly", logo: liquimolyLogo },
    { name: "Dasscompany", logo: dasscompanyLogo },
  ];

  // On duplique la liste directement dans React pour éviter les bugs de clonage DOM
  const duplicatedPartners = [...partners, ...partners];

  useEffect(() => {
    const track = trackRef.current;
    const container = containerRef.current;
    if (!track || !container) return;

    // La largeur totale d'une seule liste (la moitié de la piste dupliquée)
    const totalWidth = track.scrollWidth / 2;

    let pos = 0;
    let isPaused = false;
    let isDragging = false;
    let startX;
    let currentTranslate = 0;
    let animationId;

    const animate = () => {
      if (!isPaused && !isDragging) {
        pos += 0.8; // Vitesse de défilement
        
        // Boucle infinie fluide
        if (pos >= totalWidth) pos -= totalWidth;
        if (pos < 0) pos += totalWidth;
        
        currentTranslate = -pos;
        // translate3d permet un rendu sous-pixel (très fluide) contrairement à scrollLeft
        track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    // --- Événements Souris (Drag) ---
    const handleMouseDown = (e) => {
      isDragging = true;
      startX = e.pageX - currentTranslate;
      track.style.cursor = 'grabbing';
    };

    const handleMouseLeave = () => {
      isDragging = false;
      isPaused = false;
      track.style.cursor = 'grab';
    };

    const handleMouseUp = () => {
      isDragging = false;
      track.style.cursor = 'grab';
      pos = -currentTranslate; // Synchronise l'auto-scroll avec la nouvelle position
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX;
      currentTranslate = x - startX;
      
      // Boucle infinie pendant le drag
      if (currentTranslate <= -totalWidth) {
         currentTranslate += totalWidth;
         startX -= totalWidth;
      } else if (currentTranslate > 0) {
         currentTranslate -= totalWidth;
         startX += totalWidth;
      }
      
      track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
      pos = -currentTranslate;
    };

    // --- Événements Tactiles (Touch) ---
    const handleTouchStart = (e) => {
      isDragging = true;
      isPaused = true;
      startX = e.touches[0].pageX - currentTranslate;
    };

    const handleTouchEnd = () => {
      isDragging = false;
      isPaused = false;
      pos = -currentTranslate;
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      
      const x = e.touches[0].pageX;
      currentTranslate = x - startX;
      
      if (currentTranslate <= -totalWidth) {
         currentTranslate += totalWidth;
         startX -= totalWidth;
      } else if (currentTranslate > 0) {
         currentTranslate -= totalWidth;
         startX += totalWidth;
      }
      
      track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
      pos = -currentTranslate;
    };

    const handleMouseEnter = () => {
      isPaused = true;
    };

    // Attachement des événements
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);
    
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('mouseenter', handleMouseEnter);

    track.style.cursor = 'grab';

    return () => {
      cancelAnimationFrame(animationId);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
      
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, []);

  return (
    <div className="py-10">
      {/* touch-pan-y permet le scroll vertical natif sur mobile tout en capturant le drag horizontal */}
      <div className="relative overflow-hidden touch-pan-y" ref={containerRef}>
        {/* Effets de fondu sur les bords */}
        <div className="absolute top-0 left-0 w-20 h-full bg-gradient-to-r from-auxis-off-white dark:from-auxis-charcoal to-transparent z-10 pointer-events-none transition-colors duration-300" />
        <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-auxis-off-white dark:from-auxis-charcoal to-transparent z-10 pointer-events-none transition-colors duration-300" />

        <div className="overflow-visible whitespace-nowrap">
          {/* will-change-transform active l'accélération matérielle pour une fluidité maximale */}
          <div ref={trackRef} className="inline-block will-change-transform">
            {duplicatedPartners.map((p, i) => (
              <div key={i} className="inline-flex items-center justify-center mx-6 select-none group dark:bg-white/90 dark:px-4 dark:py-2 dark:rounded-xl transition-colors duration-300" style={{ height: '64px', width: '120px' }}>
                <img
                  src={p.logo}
                  alt={p.name}
                  title={p.name}
                  draggable="false"
                  className="max-h-12 w-auto object-contain transition-all duration-500 group-hover:scale-110 pointer-events-none"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Partenaires;
